//galaxy
import * as THREE from 'three';
import { getMouseCoords } from 'root/animator'
import { lerp } from 'root/animator/utils/lerp'
import Ease from 'root/animator/tween/easing'
import Tween from 'root/animator/tween/tween'
import { dispatchSetChanging } from '../utils'
import { layers } from '../constants';

class Object2 {
    state = {
        isLoaded: false,
        changing: false,
        visible: true
    }
    instance = new THREE.Group()
    particles1 = null;
    particles2 = null;
    geometry1 = new THREE.BufferGeometry();
    geometry2 = new THREE.BufferGeometry();
    material1 = null;
    material2 = null;
    mouse = {
        current: {x: 0, y: 0, z: 0},
        target: {x: 0, y: 0, z: 0},
    }

    parallelData = {
        position: {
            1: [],
            2: []
        },
        sizes: {
            1: [],
            2: []
        }
    }

    position = {
        visible: [ 0, 0, 0 ],
        hidden: [ 0, 0, -20 ]
    }

    count = {
        1: 120000,
        2: 1500
    }

    t0 = 0
    H = 0.1
    R = 1.9
    appearDuration: 2000
    ease = 0.05
    easeInterval = [ 0.0005, 0.2 ]

    particlePosition = [ 0, 0, -0.8 ]
    particleRotation = [ 0.6, 0, 0 ]

    canvas

    name = "Wave"

    constructor( scene, canvas ) {
        this.canvas = canvas
        this.generateWave1( )
        this.generateWave2( )
        this.instance.add( this.particles1 )
        this.instance.add( this.particles2 )
        this.instance.visible = false
        this.instance.name = this.name
        this.instance.position.set(...this.position.visible)
        this.particles1.layers.enable(layers.ENTIRE_SCENE)
        this.particles2.layers.enable(layers.ENTIRE_SCENE)
        scene.add( this.instance )
    }

    generateWave1( ) {
        let vertices = [];
        let sizes = [];
        let id = [];

        let sine = (x) => .6 * Math.sin(x) + .3 * Math.cos(2 * x) + .1 * Math.sin(4 * x);
        let noise1d = (a, r, q) => sine(10.*q * (a + 2*r));
        let fbm = (a, r) => {
            let c = 1, p = 1.3, q = 0.9;
            let f = c * noise1d(a, r, 1); c *= q;
            f = f + c * noise1d(a, r, p); c *= q;
            f = f + c * noise1d(a, r, p * p); c *= q;
            f = f + c * noise1d(a, r, p * p * p); c *= q;
            f = f / 3.43;
            return f;
        }
        for ( let i = 0; i < this.count[1]; i ++ ) {
            let r = this.R * Math.pow(Math.random(), 0.6);
            let a = Math.random() * 2 * Math.PI;
            const x = r * Math.sin(a);
            const y = this.H * fbm(a, r);
            const z = r * Math.cos(a);

            //this.parallelData.position[1].push( { x, y, z } )
            //this.parallelData.sizes[1].push( (10 + 10 * Math.random()) )

            vertices.push( x, y, z )
            sizes.push( 10 + 10 * Math.random() )
            id.push(Math.random())
        }

        this.geometry1.setAttribute( 'position', new THREE.Float32BufferAttribute( vertices, 3 ) );
        this.geometry1.setAttribute( 'size', new THREE.Float32BufferAttribute( sizes, 1 ) );
        this.geometry1.setAttribute( 'id', new THREE.Float32BufferAttribute( id, 1 ) );

        // MATERIALS

        //galaxy itself 
        const vertexShader = `
            attribute float size;
            attribute float id;
            uniform vec2 iMouse;
            varying vec3 pack;
            uniform float iAnimate;
            void main() {
                // radius
                pack.x = length(position.xz);
                // alpha
                pack.y = atan(position.z, position.x);
                // y position
                pack.z = position.y;

                vec4 mv = modelViewMatrix * vec4( position, 1.0 );
                gl_PointSize =  size / -mv.z * (.5 + .5*iAnimate);
                vec4 res = projectionMatrix * mv;

                vec2 r = res.xy - 2.5 * iMouse;
                float f = clamp(.35 - length(r), 0., 1.);
                res.xy += normalize(r) * f * f;
                
                float a = pow(iAnimate, 0.6);
                res.xy *= clamp(2. * a + pow(id, .7) - 1., 0., 1.);

                gl_Position = res;
            }`;
        const fragmentShader = `
            uniform float iTime;
            uniform float uOpacity;
            uniform float iAnimate;
            varying float r;
            varying vec3 pack;
            void main() {
                float r = pack.x;
                float a = pack.y;
                float py = pack.z;

                float glow = pow(0.018 / max(0., r), .9);
                float fading = smoothstep(-${this.H}, .8*${this.H}, py) * smoothstep(1.6 * ${this.R}, .7 * ${this.R}, r*r);
                a = sin(4. * ((-a + 2. * r)));
                float blink = clamp((.2 + 1.5 * r * sin(a)) * (.5 + .5 * sin(r * 8. + iTime * 4.)), 0., 1.);

                vec3 col = vec3(0.08, .8, 0.4) * .6;
                vec3 col2 = vec3(.2, .9, .75) * .6;
                vec3 light = vec3(0.7, .8, 1.) * .8;
                vec3 tex = 1. - smoothstep(.6, 1., vec3(length(2. * gl_PointCoord - 1.)));
                col = mix(col, col2, blink);
                
                gl_FragColor = vec4(col * tex * fading + tex * light * glow, uOpacity );
            }`;
        this.material1 = new THREE.ShaderMaterial( {
            uniforms: {
                iTime: { value: 0 },
                iMouse: { value: this.mouse.current },
                iAnimate: { value: 0 },
                uOpacity: { value: 1 }
            },
            vertexShader,
            fragmentShader,
            blending: THREE.AdditiveBlending,
            depthTest: false,
            transparent: true
        } );

        this.particles1 = new THREE.Points( this.geometry1, this.material1 );
        this.particles1.position.set(...this.particlePosition)
        this.particles1.rotation.set(...this.particleRotation)
    }
    generateWave2( ) {
        this.geometry2 = new THREE.BufferGeometry();
        let vertices2 = [];
        let sizes2 = [];
        let id2 = [];

        for ( let i = 0; i < this.count[2]; i ++ ) {
            let r = .85 * this.R * Math.sqrt(Math.random());
            let a = Math.random() * 2 * Math.PI;
            const x = r * Math.sin(a);
            const y = this.H * (r * r * 6 + 1);
            const z = r * Math.cos(a);


            //this.parallelData.position[2].push( { x, y, z } )
            //this.parallelData.sizes[2].push( (30 + 30 * Math.random()) )

            vertices2.push( x, y, z )
            sizes2.push( 30 + 30 * Math.random() )
            id2.push(Math.random())
        }

        this.geometry2.setAttribute( 'position', new THREE.Float32BufferAttribute( vertices2, 3 ) );
        this.geometry2.setAttribute( 'size', new THREE.Float32BufferAttribute( sizes2, 1 ) );
        this.geometry2.setAttribute( 'id', new THREE.Float32BufferAttribute( id2, 1 ) );

        // rocks
        const vertexShader2 = `
            attribute float size;
            attribute float id;
            uniform float iTime;
            uniform vec2 iMouse;
            uniform float iAnimate;
            varying float rn;

            void main() {
                rn = id;
                float rr = position.x * position.x + position.z * position.z;
                vec3 pos = vec3(position.x, ${this.H} * sin(iTime * .6 + 100. * rn) / (rr * .33 + .8), position.z);
                vec4 vpos = modelViewMatrix * vec4( pos, 1.0 );
                gl_PointSize =  size / -vpos.z * ( 0.8 + 0.2 * sin(iTime * (1. + rn) * 3. )) * (.5 + .5*iAnimate);
                vec4 res = projectionMatrix * vpos;

                vec2 r = res.xy - 2.5 * iMouse;
                float f = clamp(.35 - length(r), 0., 1.);
                res.xy += normalize(r) * f * f;

                float a = pow(iAnimate, 0.9);
                res.xy *= clamp(2. * a + pow(id, .7) - 1., 0., 1.);

                gl_Position = res;

            }`;
        const fragmentShader2 = `
            uniform float uOpacity;
            uniform float iAnimate;

            varying float rn;
            void main() {
            vec3 color = vec3(0.1, .9, 0.65) * .8;
            vec3 tex = 1. - smoothstep(.3, 1., vec3(length(2. * gl_PointCoord - 1.)));
            gl_FragColor = vec4(color * tex, (.5 + .5 * rn) * uOpacity);
            }`;
            this.material2 = new THREE.ShaderMaterial( {
            vertexShader: vertexShader2,
            fragmentShader: fragmentShader2,
            uniforms: this.material1.uniforms,
            blending: THREE.AdditiveBlending,
            depthTest: false,
            transparent: false
        } );



        this.particles2 = new THREE.Points( this.geometry2, this.material2 );
        this.particles2.position.set(...this.particlePosition)
        this.particles2.rotation.set(...this.particleRotation)
    }

    getSceneMousePosition(target) {
        let coord = getMouseCoords().window
    
        coord.x = coord.x * target.width  / target.clientWidth
        coord.y = coord.y * target.height / target.clientHeight
    
        let vec = new THREE.Vector3(
            coord.x / target.width  *  2 - 1,
            coord.y / target.height * -2 + 1,
        0.5
        )
    
        return vec
    }

    render__moveIn() {
        this.state.changing = true
        dispatchSetChanging(true, 0)
        Tween.to(
            {
                z: this.position.hidden[2],
                opacity: 0
            }, 
            {
                z: this.position.visible[2],
                opacity: 1
            }, 
            {
                renderDelay: 10,
                delay: 500,
                duration: 1500,
                ease: Ease.OutQuartic,
                onChange: function(context) {
                    this.instance.position.z = context.value.z
                    this.material1.uniforms.uOpacity.value = context.value.opacity
                    this.material2.uniforms.uOpacity.value = context.value.opacity
                }.bind(this),
                onComplete: function() { 
                    this.state.changing = false 
                    this.state.visible = true
                    dispatchSetChanging(false)
                }.bind(this)
            }
        )
    }
    render__moveOut() {
        this.state.changing = true
        dispatchSetChanging(true, 0)
        Tween.to(
            {
                z: this.position.visible[2],
                opacity: 1
            }, 
            {
                z: this.position.hidden[2],
                opacity: 0
            }, 
            {
                renderDelay: 10,
                duration: 2500,
                ease: Ease.InQuartic,
                onChange: function(context) {
                    this.instance.position.z = context.value.z
                    this.material1.uniforms.uOpacity.value = context.value.opacity
                }.bind(this),
                onComplete: function() { 
                    this.state.changing = false 
                    this.state.visible = false
                    dispatchSetChanging(false)
                }.bind(this)
            }
        )
    }

    render({ visible = true }) {
        if ( !this.state.isLoaded ) {
            const from = 0
            const to = 1
            Tween.to(from, to,
                {
                    ease: Ease.InOutCubic,
                    duration: 2000,
                    onChange: function(context) {
                        let a = context.value;
                        this.material1.uniforms[ 'iAnimate' ].value = a;
                    }.bind(this),
                        onComplete: function(context) { 
                    }.bind(this)
                }
            )
            this.state.isLoaded = true
        }

        if ( !this.state.changing ) {
            if ( !visible && this.state.visible ) {
                this.render__moveOut()
            }
            if ( visible && !this.state.visible ) {
                this.render__moveIn()
            }
        }

        if ( this.state.visible || visible ) {
            let t = performance.now() / 1000;
            let dt = t - this.t0;
            this.particles1.rotation.y += 0.15 * dt;
            this.particles2.rotation.y += 0.15 * dt;
            this.material1.uniforms[ 'iTime' ].value = t;
            this.material1.uniforms[ 'iMouse' ].value = this.mouse.current;
            this.t0 = t;
    
            this.mouse.target = this.getSceneMousePosition(this.canvas)
            this.mouse.current.x = lerp(this.mouse.current.x, this.mouse.target.x, 0.09)
            this.mouse.current.y = lerp(this.mouse.current.y, this.mouse.target.y, 0.09)

            this.instance.visible = true
        } else {
            this.instance.visible = false
        }
    }
}

export default Object2